import { getPlanningRowKey } from './kkpPlanningMergeUtils';
import { getPlanningWeekKey } from './kkpPlanningWeekUtils';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Clé client stable : tiers Dolibarr si possible, sinon libellé normalisé. */
export function getClientPresenceKey(row) {
  const thirdpartyId = Number(row?.dolibarr_thirdparty_id);
  if (Number.isFinite(thirdpartyId) && thirdpartyId > 0) {
    return `id:${thirdpartyId}`;
  }
  const label = String(row?.client_label ?? '').trim().toLowerCase();
  return label ? `label:${label}` : null;
}

/** Clé emplacement : une série « N sem. » ne se compte que sur le même format. */
export function getEmplacementPresenceKey(row) {
  const label = String(row?.emplacement ?? row?.format ?? '').trim().toLowerCase();
  return label || null;
}

/** Ligne qui compte comme présence client (hors Indispo / placeholder vide). */
export function isClientPresenceRow(row) {
  if (!getClientPresenceKey(row)) return false;
  const statut = String(row?.statut_phase ?? '').trim().toLowerCase();
  if (statut === 'indispo') return false;
  return true;
}

function parseWeekDate(row) {
  const key = String(row?.semaine_date ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const date = new Date(`${key}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Deux semaines calendaires d'affilée (~7 jours, tolérance DST). */
export function areConsecutiveWeekDates(dateA, dateB) {
  if (!dateA || !dateB) return false;
  const diff = (dateB.getTime() - dateA.getTime()) / DAY_MS;
  return diff >= 6 && diff <= 8;
}

/**
 * Pour chaque ligne d'une série multi-semaines (même client **et** même emplacement),
 * indique le total et la position chronologique (semaines avant / après)
 * au sein de la série continue. Un même client sur un autre emplacement ne compte pas.
 * @returns {Map<string, { totalWeeks: number, weeksBefore: number, weeksAfter: number, position: number, weekLabels: string[] }>}
 */
export function buildClientWeekSpanByRowKey(ventes, getRowKey = getPlanningRowKey) {
  const map = new Map();
  /** @type {Map<string, Map<string, { weekKey: string, date: Date, label: string, rowKeys: string[] }>>} */
  const byClientEmplacement = new Map();

  for (const row of ventes ?? []) {
    if (!isClientPresenceRow(row)) continue;
    const clientKey = getClientPresenceKey(row);
    const emplacementKey = getEmplacementPresenceKey(row);
    const date = parseWeekDate(row);
    if (!clientKey || !date) continue;

    const seriesKey = `${clientKey}|emp:${emplacementKey ?? ''}`;
    const weekKey = getPlanningWeekKey(row);
    if (!byClientEmplacement.has(seriesKey)) byClientEmplacement.set(seriesKey, new Map());
    const weeks = byClientEmplacement.get(seriesKey);
    if (!weeks.has(weekKey)) {
      weeks.set(weekKey, {
        weekKey,
        date,
        label: String(row.semaine || weekKey).trim(),
        rowKeys: [],
      });
    }
    weeks.get(weekKey).rowKeys.push(getRowKey(row));
  }

  for (const weeksMap of byClientEmplacement.values()) {
    const weeks = [...weeksMap.values()].sort((a, b) => a.date - b.date);
    let streak = [];

    const flushStreak = () => {
      if (streak.length < 2) {
        streak = [];
        return;
      }
      const weekLabels = streak.map((week) => week.label);
      streak.forEach((week, index) => {
        const info = {
          totalWeeks: streak.length,
          weeksBefore: index,
          weeksAfter: streak.length - 1 - index,
          position: index + 1,
          weekLabels,
        };
        week.rowKeys.forEach((rowKey) => {
          map.set(rowKey, info);
        });
      });
      streak = [];
    };

    for (const week of weeks) {
      if (streak.length === 0) {
        streak = [week];
        continue;
      }
      const prev = streak[streak.length - 1];
      if (areConsecutiveWeekDates(prev.date, week.date)) {
        streak.push(week);
      } else {
        flushStreak();
        streak = [week];
      }
    }
    flushStreak();
  }

  return map;
}

/**
 * Données d’affichage pour la badge Client (libellé court + détail tooltip).
 * @returns {null | {
 *   label: string,
 *   totalWeeks: number,
 *   weeksBefore: number,
 *   weeksAfter: number,
 *   position: number,
 *   weekLabels: string[],
 *   relativeSummary: string,
 * }}
 */
export function formatClientWeekSpanBadge(span) {
  if (!span || span.totalWeeks < 2) return null;

  const { totalWeeks, weeksBefore, weeksAfter, position, weekLabels } = span;
  let relativeSummary;
  if (weeksBefore === 0) {
    relativeSummary = weeksAfter === 1
      ? '1 semaine après cette ligne'
      : `${weeksAfter} semaines après cette ligne`;
  } else if (weeksAfter === 0) {
    relativeSummary = weeksBefore === 1
      ? '1 semaine avant cette ligne'
      : `${weeksBefore} semaines avant cette ligne`;
  } else {
    const before = weeksBefore === 1 ? '1 semaine avant' : `${weeksBefore} semaines avant`;
    const after = weeksAfter === 1 ? '1 semaine après' : `${weeksAfter} semaines après`;
    relativeSummary = `${before} et ${after}`;
  }

  return {
    label: `${totalWeeks} sem.`,
    totalWeeks,
    weeksBefore,
    weeksAfter,
    position,
    weekLabels,
    relativeSummary,
  };
}
